"use client";

import React from "react";
import { Mail, Phone, MapPin, Clock, Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-white/50 rounded-xl border border-[var(--border)] animate-in fade-in zoom-in duration-500">
        <div className="size-12 rounded-full bg-[hsl(85_15%_45%)]/10 flex items-center justify-center text-[hsl(85_15%_45%)]">
          <Send className="size-6" />
        </div>
        <h3 className="text-xl font-serif font-medium text-[var(--foreground)]">Message Sent</h3>
        <p className="text-[var(--muted-foreground)]">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-2 border-[hsl(85_15%_45%)] text-[hsl(85_15%_45%)] hover:bg-[hsl(85_15%_45%)] hover:text-white">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[var(--foreground)]">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" {...field} className="bg-white/50 border-[var(--border)] focus-visible:ring-[hsl(85_15%_45%)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[var(--foreground)]">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="bg-white/50 border-[var(--border)] focus-visible:ring-[hsl(85_15%_45%)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[var(--foreground)]">Subject</FormLabel>
              <FormControl>
                <Input placeholder="Brief subject of your message" {...field} className="bg-white/50 border-[var(--border)] focus-visible:ring-[hsl(85_15%_45%)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[var(--foreground)]">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" {...field} className="bg-white/50 border-[var(--border)] focus-visible:ring-[hsl(85_15%_45%)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[var(--foreground)]">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="How can we help you?" {...field} className="min-h-[150px] bg-white/50 border-[var(--border)] focus-visible:ring-[hsl(85_15%_45%)] resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-red-50 border border-red-100 rounded-md text-red-600 text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base bg-[hsl(85_15%_45%)] hover:bg-[hsl(85_15%_40%)] text-white shadow-sm transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function ReachOut() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="reach-out" className="relative w-full py-20 md:py-32 bg-[var(--background)] overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {}
          <div className="lg:col-span-4 space-y-10">
            <div className="space-y-4">
              <span className="inline-block text-sm font-medium tracking-wider text-[hsl(85_15%_45%)] uppercase">
                Get in Touch
              </span>
              <h2 className="text-4xl md:text-5xl font-serif text-[var(--foreground)] leading-tight">
                Hello.
              </h2>
              <p className="text-[var(--muted-foreground)] text-lg leading-relaxed">
                We’d love to hear from you. Whether you have a question about organization, a collaboration idea, or just want to say hi, drop us a message.
              </p>
            </div>

            <div className="space-y-6 pt-4 border-t border-[var(--border)]">
              

              <div className="flex items-start gap-4">
                <div className="mt-1 p-2 rounded-full bg-[hsl(85_15%_45%)]/10 text-[hsl(85_15%_45%)]">
                  <Mail className="size-5" />
                </div>
                <div>
                  <h4 className="font-medium text-[var(--foreground)]">Email Us</h4>
                  <p className="text-[var(--muted-foreground)] text-sm mt-1">
                    info@theorganizedspace.com
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="mt-1 p-2 rounded-full bg-[hsl(85_15%_45%)]/10 text-[hsl(85_15%_45%)]">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <h4 className="font-medium text-[var(--foreground)]">Location</h4>
                  <p className="text-[var(--muted-foreground)] text-sm mt-1">
                    123 Main St, Springfield
                  </p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-8 bg-white rounded-2xl p-6 md:p-10 shadow-sm border border-[var(--border)]">
            <div className="mb-8">
              <h3 className="text-2xl font-serif text-[var(--foreground)] mb-2">Send a Message</h3>
              <p className="text-[var(--muted-foreground)]">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
            </div>

            <FormProvider formId="contact_form_contact" sectionName="reach-out" defaultValues={{
            name: "",
            email: "",
            subject: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true
            }
          }} submitText="Send Message" successMessage="Thank you for reaching out! We've received your message and will get back to you shortly.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}